import { Fragment, useEffect, useState } from "react";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import { useTheme, Theme } from "@emotion/react";
import { ThuiswinkelWaarborgIconWhite } from "src/icons/ThuiswinkelWaarborgIconWhite";
import { MastercardIconWhite } from "src/icons/MastercardIconWhite";
import { IdealIconWhite } from "src/icons/IdealIconWhite";
import { PaypalIconWhite } from "src/icons/PaypalIconWhite";
import { VisaIconWhite } from "src/icons/VisaIconWhite";
import { AmexIconWhite } from "src/icons/AmexIconWhite";
import { ApplePayIconWhite } from "src/icons/ApplePayIconWhite";
import { BancontactWhite } from "src/icons/BancontactWhite";
import { CartaSiIconWhite } from "src/icons/CartaSiIconWhite";
import { MaestroIconWhite } from "src/icons/MaestroIconWhite";
import { PostepayIconWhite } from "src/icons/PostepayIconWhite";
import { Przelewy24IconWhite } from "src/icons/Przelewy24IconWhite";
import { SofortIconWhite } from "src/icons/SofortIconWhite";

import { TrustpilotCustom } from "src/molecules/TrustpilotBox";
import { Locale } from "src/lib/Intl";
import { Divider } from "src/atoms/Divider";
import styles from "./Footer.module.scss";
import { BCorpLogo } from "src/atoms/BCorpLogo/BCorpLogo";
import { KlarnaIconWhite } from "src/icons/KlarnaIconWhite";

const paymentMethodsMap = {
  paypal: <PaypalIconWhite data-testid="paypal-icon" />,
  ideal: <IdealIconWhite data-testid="ideal-icon" />,
  amex: <AmexIconWhite data-testid="amex-icon" />,
  bancontact: <BancontactWhite data-testid="bancontact-icon" />,
  visa: <VisaIconWhite data-testid="visa-icon" />,
  "apple-pay": <ApplePayIconWhite data-testid="apple-pay-icon" />,
  mastercard: <MastercardIconWhite data-testid="mastercard-icon" />,
  maestro: <MaestroIconWhite data-testid="maestro-icon" />,
  "carta-si": <CartaSiIconWhite data-testid="carta-si-icon" />,
  "poste-pay": <PostepayIconWhite data-testid="poste-pay-icon" />,
  "przelewy-24": <Przelewy24IconWhite data-testid="przelewy-24-icon" />,
  sofort: <SofortIconWhite data-testid="sofort-icon" />,
  klarna: <KlarnaIconWhite data-testid="klarna-icon" />,
  thuiswinkel: (
    <Box display={["none", "none", "none", "block"]}>
      <a
        rel="noopener noreferrer"
        href="https://www.thuiswinkel.org/leden/otrium-nl/certificaat/"
        target="_blank"
      >
        <ThuiswinkelWaarborgIconWhite data-testid="tw-icon" />
      </a>
    </Box>
  ),
};

type PaymentMethodKeys = keyof typeof paymentMethodsMap;

const paymentMethodsConfig: Record<Locale, PaymentMethodKeys[]> = {
  en: ["mastercard", "ideal", "bancontact", "paypal", "visa"],
  nl: ["thuiswinkel", "mastercard", "ideal", "bancontact", "paypal", "visa"],
  fr: ["paypal", "visa", "mastercard"],
  de: ["klarna", "sofort", "paypal", "visa", "mastercard"],
  "de-at": ["sofort", "paypal", "visa", "mastercard"],
  "en-dk": ["paypal", "visa", "mastercard", "amex"],
  "en-gb": ["visa", "mastercard"],
  "en-se": ["paypal", "visa", "mastercard", "amex", "apple-pay"],
  "en-us": ["paypal", "visa", "mastercard", "amex"],
  "it-it": [
    "paypal",
    "visa",
    "mastercard",
    "amex",
    "apple-pay",
    "carta-si",
    "poste-pay",
  ],
  "pl-pl": ["paypal", "visa", "mastercard", "amex", "apple-pay", "przelewy-24"],
  "es-es": ["paypal", "visa", "mastercard", "amex", "apple-pay", "maestro"],
  "nl-be": ["mastercard", "ideal", "bancontact", "paypal", "visa"],
};

type FooterHighlightProps = {
  locale: Locale;
};

function FooterHighlight({ locale }: FooterHighlightProps): JSX.Element {
  const theme: Theme = useTheme();
  const { isMobile } = useBreakpoints();
  const [isClient, setClient] = useState(false);

  useEffect(() => {
    setClient(true);
  }, []);

  return (
    <div className={styles.highlightWrapper} data-testid="footer-top-payments">
      <div className={styles.hideFromDesktop}>
        <Flex
          variant="centered"
          justifyContent={["space-between", "space-between", "space-around"]}
        >
          {locale === "nl" && !isMobile && isClient ? (
            <a
              rel="noopener noreferrer"
              href="https://www.thuiswinkel.org/leden/otrium-nl/certificaat/"
              target="_blank"
            >
              <ThuiswinkelWaarborgIconWhite data-testid="tw-icon" />
            </a>
          ) : null}
          <TrustpilotCustom locale={locale} />
          <BCorpLogo locale={locale} />
        </Flex>
        <Box my={"23px"}>
          <Divider color={theme.colors.tone.anthraciteDark} />
        </Box>
      </div>
      <div className={styles.highlightIcons}>
        {paymentMethodsConfig[locale]?.map((paymentMethod) => (
          <Fragment key={paymentMethod}>
            {paymentMethodsMap[paymentMethod]}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export { FooterHighlight };
